/*
 * EaseFlow shortcuts section (sections/flow-keys.liquid): plays itself
 * while it's on screen. Each round presses one row's keys - they show in
 * the Program monitor and the row lights up - EaseFlow's keyframes pop in
 * between the two on the timeline and the toast says which curve, then
 * the playhead plays the pair: the clip moves with that curve and leaves
 * onion-skin copies behind that show its spacing. The next round goes
 * back the other way with the next row. With reduced motion it shows the
 * first round as it ends.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const apple = /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');

  const bezier = (x1, y1, x2, y2) => {
    const at = (a, b, t) => 3 * a * t * (1 - t) * (1 - t) + 3 * b * t * t * (1 - t) + t * t * t;
    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let lo = 0;
      let hi = 1;
      for (let i = 0; i < 30; i += 1) {
        const mid = (lo + hi) / 2;
        if (at(x1, x2, mid) < x) lo = mid;
        else hi = mid;
      }
      return at(y1, y2, (lo + hi) / 2);
    };
  };

  // a damped spring that comes to rest on the second keyframe
  const bouncy = (damping, frequency) => {
    const response = (t) => 1 - Math.exp(-damping * t) * (Math.cos(frequency * t) + (damping / frequency) * Math.sin(frequency * t));
    const rest = 1 - response(1);
    return (x) => (x <= 0 ? 0 : x >= 1 ? 1 : response(x) + rest * x * x * (3 - 2 * x));
  };

  const curveOf = (text) => {
    if (/bounc/i.test(text)) return { ease: bouncy(6, 11), bouncy: true };
    const n = String(text).split(',').map(Number);
    if (n.length === 4 && n.every((v) => !Number.isNaN(v))) return { ease: bezier(n[0], n[1], n[2], n[3]), bouncy: false };
    return { ease: (x) => x, bouncy: false };
  };

  const thumbPath = (ease) => {
    let d = '';
    for (let i = 0; i <= 32; i += 1) {
      const x = i / 32;
      d += `${i ? 'L' : 'M'}${(3 + x * 34).toFixed(1)} ${(27 - ease(x) * 24).toFixed(1)}`;
    }
    return d;
  };

  const timecode = (frames) => `00:00:${String(Math.floor(frames / 24)).padStart(2, '0')}:${String(Math.floor(frames % 24)).padStart(2, '0')}`;

  const setup = (section) => {
    if (section.dataset.fkReady) return;
    section.dataset.fkReady = '1';
    const $ = (name) => section.querySelector(`[data-fk="${name}"]`);
    const $$ = (name) => Array.from(section.querySelectorAll(`[data-fk="${name}"]`));
    const stage = $('stage');
    if (!stage) return;
    const rows = $$('row').map((row) => Object.assign({ row, name: row.dataset.name, key: row.dataset.key, f: row.dataset.f === 'true' }, curveOf(row.dataset.curve)));
    if (!rows.length) return;
    const clip = $('clip');
    const ghosts = $$('ghost');
    const hud = $('hud');
    const toast = $('toast');
    const toastText = $('toast-text');
    const lane = $('lane');
    const playhead = $('playhead');
    const tc = $('tc');
    const prefix = toastText.textContent.replace(rows[0].name, '').trim();
    const modifier = apple ? '⌥' : 'Alt';

    $$('mod').forEach((cap) => {
      cap.textContent = modifier;
    });
    rows.forEach((item) => {
      const path = item.row.querySelector('[data-fk="thumb"]');
      if (path) path.setAttribute('d', thumbPath(item.ease));
    });

    const LEFT = 4;
    const RIGHT = 96;
    const ticks = [];
    const showTicks = (count) => {
      ticks.splice(0).forEach((tick) => tick.remove());
      for (let i = 1; i <= count; i += 1) {
        const tick = document.createElement('span');
        tick.className = playhead.className.split(' ')[0].replace('playhead', 'tick');
        tick.style.setProperty('--at', `${LEFT + ((RIGHT - LEFT) * i) / (count + 1)}%`);
        lane.insertBefore(tick, playhead);
        ticks.push(tick);
      }
    };

    const place = (item, x, forward) => {
      const v = item.ease(x);
      return forward ? v : 1 - v;
    };

    const settle = (item, forward) => {
      rows.forEach((other) => other.row.classList.toggle('is-on', other === item));
      showTicks(item.bouncy ? 12 : 7);
      ticks.forEach((tick) => tick.classList.add('is-on'));
      ghosts.forEach((ghost, i) => {
        ghost.style.setProperty('--fk-x', place(item, i / (ghosts.length - 1), forward));
        ghost.classList.add('is-on');
      });
      clip.style.setProperty('--fk-x', forward ? 1 : 0);
      playhead.style.setProperty('--ph', `${RIGHT}%`);
      tc.textContent = timecode(24);
      toastText.textContent = `${prefix} ${item.name}`;
      toast.classList.add('is-on');
    };

    if (still.matches) {
      settle(rows[0], true);
      return;
    }

    let round = 0;
    let running = false;
    let token = 0;
    const wait = (ms, mine) => new Promise((done) => setTimeout(() => done(mine === token), ms));
    const frame = () => new Promise((done) => requestAnimationFrame(done));

    const capClass = section.querySelector('[class*="flow-keys__cap-"]').className.split(' ')[0];
    const press = (item) => {
      hud.innerHTML = '';
      return (item.f ? [item.key] : [modifier, item.key]).map((label) => {
        const cap = document.createElement('span');
        cap.className = capClass;
        cap.textContent = label;
        hud.appendChild(cap);
        return cap;
      });
    };

    const play = async (mine) => {
      while (running && mine === token) {
        const item = rows[round % rows.length];
        const forward = round % 2 === 0;
        round += 1;

        // keys go down; EaseFlow eases the pair
        const caps = press(item);
        const rowCaps = Array.from(item.row.getElementsByClassName(capClass));
        hud.classList.add('is-on');
        if (!(await wait(160, mine))) return;
        caps.concat(rowCaps).forEach((cap) => cap.classList.add('is-down'));
        rows.forEach((other) => other.row.classList.toggle('is-on', other === item));
        toast.classList.remove('is-on');
        ghosts.forEach((ghost) => ghost.classList.remove('is-on'));
        showTicks(item.bouncy ? 12 : 7);
        playhead.style.setProperty('--ph', `${LEFT}%`);
        tc.textContent = timecode(0);
        for (let i = 0; i < ticks.length; i += 1) {
          if (!(await wait(22, mine))) return;
          ticks[i].classList.add('is-on');
        }
        toastText.textContent = `${prefix} ${item.name}`;
        toast.classList.add('is-on');
        if (!(await wait(220, mine))) return;
        caps.concat(rowCaps).forEach((cap) => cap.classList.remove('is-down'));

        // play it back
        const duration = item.bouncy ? 1500 : 1150;
        const start = performance.now();
        let shown = 0;
        for (;;) {
          await frame();
          if (mine !== token) return;
          const x = Math.min(1, (performance.now() - start) / duration);
          clip.style.setProperty('--fk-x', place(item, x, forward));
          playhead.style.setProperty('--ph', `${LEFT + (RIGHT - LEFT) * x}%`);
          tc.textContent = timecode(24 * x);
          while (shown < ghosts.length && x >= shown / (ghosts.length - 1)) {
            ghosts[shown].style.setProperty('--fk-x', place(item, shown / (ghosts.length - 1), forward));
            ghosts[shown].classList.add('is-on');
            shown += 1;
          }
          if (x >= 1) break;
          if (x > 0.35) hud.classList.remove('is-on');
        }
        hud.classList.remove('is-on');
        if (!(await wait(1500, mine))) return;
      }
    };

    const startLoop = () => {
      if (running) return;
      running = true;
      token += 1;
      play(token);
    };
    const stopLoop = () => {
      running = false;
      token += 1;
    };

    settle(rows[rows.length - 1], false);
    round = 0;
    let visible = false;
    const update = () => (visible && !document.hidden ? startLoop() : stopLoop());
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        visible = entries[entries.length - 1].isIntersecting;
        update();
      }, { threshold: 0.3 }).observe(stage);
    } else {
      visible = true;
    }
    document.addEventListener('visibilitychange', update);
    update();
  };

  const init = (root) => root.querySelectorAll('[data-flow-keys]').forEach(setup);
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
